import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Check, Loader2, AlertCircle, Download } from "lucide-react";
import SEO from "../components/SEO";

export default function OrderSuccess() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const orderParam = search.get("order");
  const [status, setStatus] = useState(sessionId ? "polling" : (orderParam ? "manual" : "unknown"));
  const [orderNumber, setOrderNumber] = useState(orderParam || null);
  const [attempts, setAttempts] = useState(0);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    const poll = async (n = 0) => {
      if (n >= 10) { setStatus("timeout"); return; }
      try {
        const r = await api.get(`/orders/status/${sessionId}`);
        if (!active) return;
        setOrderNumber(r.data.order_number);
        if (r.data.payment_status === "paid") setStatus("paid");
        else if (r.data.payment_status === "expired") setStatus("expired");
        else { setAttempts(n + 1); setTimeout(() => poll(n + 1), 2000); }
      } catch {
        setAttempts(n + 1); setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="pt-10 pb-20 px-4" data-testid="order-success-page">
      <SEO title="Děkujeme za nákup" noindex />
      <div className="max-w-xl mx-auto text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-6 animate-spin"/>
            <h1 className="font-display text-3xl font-bold mb-3">Zpracováváme platbu…</h1>
            <p className="text-zinc-600">Pokus {attempts + 1}/10. Prosím vyčkejte.</p>
          </>
        )}
        {(status === "paid" || status === "manual") && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-600"/>
            </div>
            <h1 className="font-display text-4xl font-bold mb-3">Děkujeme!</h1>
            <p className="text-zinc-600 mb-1">Vaše objednávka <strong>{orderNumber}</strong> byla úspěšně přijata.</p>
            <p className="text-zinc-600 mb-8">{status === "paid" ? "Licenční klíče jsme odeslali na váš e-mail a najdete je v sekci Můj účet → Licence." : "Platební údaje jsme zaslali na váš e-mail. Klíče obdržíte po připsání platby."}</p>
            {status === "paid" && orderNumber && <OrderSuccessDownloads sessionId={sessionId}/>}
            {status === "manual" && orderNumber && (
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-8 text-left max-w-md mx-auto" data-testid="bank-transfer-info">
                <div className="font-display font-bold mb-3">Platební údaje</div>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <div className="text-zinc-600">Číslo účtu:</div><div className="font-mono font-semibold">{settings?.bank_account || "670100-2216031489/6210"}</div>
                  <div className="text-zinc-600">IBAN:</div><div className="font-mono font-semibold text-xs">{settings?.bank_iban || "CZ69 6210 6701 0022 1603 1489"}</div>
                  <div className="text-zinc-600">Variabilní symbol:</div><div className="font-mono font-semibold">{orderNumber.replace(/-/g, "")}</div>
                  <div className="text-zinc-600">Zpráva pro příjemce:</div><div className="font-mono font-semibold">{orderNumber}</div>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Link to="/ucet" className="btn-primary px-6 py-3 rounded-full font-semibold" data-testid="goto-account">Můj účet</Link>
              <Link to="/produkty" className="px-6 py-3 rounded-full font-semibold border border-zinc-200 hover:bg-zinc-50">Pokračovat v nákupu</Link>
            </div>
          </>
        )}
        {(status === "timeout" || status === "expired") && (
          <>
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-6"/>
            <h1 className="font-display text-3xl font-bold mb-3">Něco se pokazilo</h1>
            <p className="text-zinc-600 mb-6">Platbu se nepodařilo potvrdit. Zkontrolujte si e-mail nebo nás kontaktujte.</p>
            <Link to="/kontakt" className="btn-primary px-6 py-3 rounded-full font-semibold">Kontaktovat podporu</Link>
          </>
        )}
      </div>
    </div>
  );
}

function OrderSuccessDownloads({ sessionId }) {
  const [files, setFiles] = useState([]);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await api.get(`/orders/status/${sessionId}`);
        if (!active) return;
        const oid = s.data?.order_id;
        if (!oid) return;
        setOrderId(oid);
        const r = await api.get(`/orders/${oid}/files`);
        if (active) setFiles(r.data || []);
      } catch { /* unauthenticated guest – skip */ }
    })();
    return () => { active = false; };
  }, [sessionId]);

  const fmtSize = (n) => {
    if (!n) return "";
    const u = ["B","KB","MB","GB"]; let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
  };

  const openFile = async (fid, slug) => {
    if (slug) {
      window.open(`/download/${slug}`, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const { data } = await api.get(`/orders/${orderId}/files/${fid}/url`);
      const a = document.createElement("a");
      a.href = data.url; a.target = "_blank"; a.rel = "noopener noreferrer";
      a.download = data.filename || "";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* ignore */ }
  };

  if (!files.length) return null;
  return (
    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-8 text-left max-w-md mx-auto" data-testid="order-success-downloads">
      <div className="font-display font-bold mb-3 text-sm">Instalační soubory ke stažení</div>
      <div className="space-y-2">
        {files.map((f) => (
          <button
            key={f.id}
            onClick={() => openFile(f.id, f.slug)}
            className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-white hover:bg-blue-100 transition-colors text-sm"
            data-testid={`success-download-${f.id}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Download className="w-4 h-4 text-blue-600 shrink-0"/>
              <span className="font-semibold text-zinc-900 truncate">{f.title || f.filename}</span>
            </div>
            {f.size ? <span className="text-xs text-zinc-500 shrink-0">{fmtSize(f.size)}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}
